import React, { forwardRef, useMemo } from "react";
import DatePicker from "react-datepicker";
import { Icon } from "@iconify/react";
import "react-datepicker/dist/react-datepicker.css";
import "./AppDatePicker.css";

// Convert YYYY-MM-DD <-> Date without dragging in the user's local-tz offset
// (which would shift the calendar day by one near midnight in non-UTC zones).
const toDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toIso = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const TriggerInput = forwardRef(({ value, onClick, placeholder, disabled, ariaLabel }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    disabled={disabled}
    className="app-dp-trigger"
    aria-label={ariaLabel || placeholder || "Select date"}
  >
    <Icon icon="solar:calendar-bold-duotone" className="app-dp-trigger-icon" />
    <span className={`app-dp-trigger-text${value ? "" : " is-placeholder"}`}>
      {value || placeholder || "Select date"}
    </span>
    <Icon icon="solar:alt-arrow-down-linear" className="app-dp-trigger-caret" />
  </button>
));
TriggerInput.displayName = "TriggerInput";

const renderHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => {
  const monthLabel = date.toLocaleString("en-US", { month: "long", year: "numeric" });
  return (
    <div className="app-dp-header">
      <button
        type="button"
        className="app-dp-nav"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        <Icon icon="solar:alt-arrow-left-linear" />
      </button>
      <span className="app-dp-title">{monthLabel}</span>
      <button
        type="button"
        className="app-dp-nav"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        <Icon icon="solar:alt-arrow-right-linear" />
      </button>
    </div>
  );
};

// String-in / string-out wrapper around react-datepicker so it's a drop-in
// replacement for native <input type="date"> with YYYY-MM-DD values.
const AppDatePicker = ({
  value,
  onChange,
  min,
  max,
  placeholder = "YYYY-MM-DD",
  disabled = false,
  ariaLabel,
  popperPlacement = "bottom-start",
}) => {
  const selected = useMemo(() => toDate(value), [value]);
  const minDate = useMemo(() => toDate(min), [min]);
  const maxDate = useMemo(() => toDate(max), [max]);

  return (
    <DatePicker
      selected={selected}
      onChange={(date) => onChange(toIso(date))}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      dateFormat="yyyy-MM-dd"
      popperPlacement={popperPlacement}
      showPopperArrow={false}
      calendarClassName="app-dp-calendar"
      wrapperClassName="app-dp-wrapper"
      renderCustomHeader={renderHeader}
      customInput={<TriggerInput placeholder={placeholder} ariaLabel={ariaLabel} />}
    />
  );
};

export default AppDatePicker;
